import config from "@app/lib/api/config";
import { useExportFrameAsPdf } from "@app/lib/swr/frames";
import { useIsMobile } from "@app/lib/swr/useIsMobile";
import { isFrameV2ContentType } from "@app/types/files";
import type { LightWorkspaceType } from "@app/types/user";
import { datadogLogs } from "@datadog/browser-logs";
import {
  Button,
  Download01,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@dust-tt/sparkle";
import { Trans, useLingui } from "@lingui/react/macro";
import type React from "react";
import { useState } from "react";

interface ExportContentDropdownProps {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  owner: LightWorkspaceType;
  fileId: string;
  fileContent: string | null;
  fileName?: string;
  contentType?: string;
}

export function ExportContentDropdown({
  iframeRef,
  owner,
  fileId,
  fileContent,
  fileName,
  contentType,
}: ExportContentDropdownProps) {
  const { t } = useLingui();
  const isMobile = useIsMobile();
  const exportAsPdf = useExportFrameAsPdf({ owner });
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const exportLabel = isExportingPdf ? t`Exporting...` : t`Export`;

  const handleExportAsPng = () => {
    if (fileContent) {
      const imgRegex = /<img[^>]+src=["'](https?:\/\/[^"']+)["']/gi;
      if (imgRegex.test(fileContent)) {
        return;
      }
    }

    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: `EXPORT_PNG` }, "*");
    } else {
      datadogLogs.logger.info(
        "Failed to export content as PNG: No iframe content window found"
      );
    }
  };

  const handleExportAsPdf = async (orientation: "portrait" | "landscape") => {
    if (isExportingPdf) {
      return;
    }
    setIsExportingPdf(true);
    await exportAsPdf({ fileId, fileName, orientation });
    setIsExportingPdf(false);
  };

  // A Frames v2 package downloads as a ZIP of its whole source folder, a legacy Frame as its
  // single code file.
  const codeDownloadLabel =
    contentType && isFrameV2ContentType(contentType)
      ? t`Source (.zip)`
      : t`Template`;

  const handleDownloadAsCode = () => {
    const downloadUrl = `${config.getApiBaseUrl()}/api/w/${owner.sId}/files/${fileId}?action=download`;
    window.open(downloadUrl, "_blank");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          icon={Download01}
          isSelect
          label={isMobile ? undefined : exportLabel}
          tooltip={isMobile ? exportLabel : undefined}
          variant="ghost"
          disabled={isExportingPdf}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled={isExportingPdf} label="PDF" />
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => handleExportAsPdf("portrait")}>
              <Trans>Portrait</Trans>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExportAsPdf("landscape")}>
              <Trans>Landscape</Trans>
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem onClick={handleExportAsPng}>PNG</DropdownMenuItem>
        <DropdownMenuItem onClick={handleDownloadAsCode}>
          {codeDownloadLabel}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
